import { useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type ApplicationOut,
  type ApplicationStatus,
  approveApplication,
  listApplications,
  rejectApplication,
} from "@/api/applications";
import { usePermission } from "@/auth/usePermission";
import { PageHeader } from "@/components/PageHeader";

const STATUS_COLOR: Record<
  string,
  "default" | "primary" | "success" | "warning" | "error" | "info"
> = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "error",
  CANCELLED: "default",
};

export function ApplicationsAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canReview = usePermission("user:create");

  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | "ALL">(
    "PENDING",
  );
  const [target, setTarget] = useState<ApplicationOut | null>(null);
  const [action, setAction] = useState<"approve" | "reject" | null>(null);
  const [note, setNote] = useState("");
  const [createInvite, setCreateInvite] = useState(true);
  const [membershipKind, setMembershipKind] = useState<
    "SALARIE" | "LIBERAL" | "VACATAIRE" | "STAGIAIRE" | "RESERVISTE"
  >("LIBERAL");
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  const apps = useQuery({
    queryKey: ["practitioner-applications", statusFilter],
    queryFn: () =>
      listApplications(
        statusFilter === "ALL" ? undefined : (statusFilter as ApplicationStatus),
      ),
    enabled: canReview,
  });

  const approveMutation = useMutation({
    mutationFn: () =>
      approveApplication(target!.id, {
        create_user_invite: createInvite,
        membership_kind: membershipKind,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      setTarget(null);
      setAction(null);
      setNote("");
      setErrorDetail(null);
      qc.invalidateQueries({ queryKey: ["practitioner-applications"] });
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } }).response
        ?.data?.detail;
      setErrorDetail(
        typeof detail === "string" ? detail : JSON.stringify(detail) ?? "error",
      );
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectApplication(target!.id, note.trim() || undefined),
    onSuccess: () => {
      setTarget(null);
      setAction(null);
      setNote("");
      setErrorDetail(null);
      qc.invalidateQueries({ queryKey: ["practitioner-applications"] });
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } }).response
        ?.data?.detail;
      setErrorDetail(typeof detail === "string" ? detail : "error");
    },
  });

  if (!canReview) {
    return (
      <Box>
        <PageHeader title={t("applications.title")} subtitle={t("applications.subtitle")} helpTerm="candidature" />
        <Alert severity="warning">{t("applications.forbidden")}</Alert>
      </Box>
    );
  }

  const rows = apps.data?.items ?? [];

  return (
    <Box>
      <PageHeader
        title={t("applications.title")}
        subtitle={t("applications.subtitle")}
        actions={
          <IconButton onClick={() => apps.refetch()} aria-label={t("common.retry")}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t("applications.statusFilter")}</InputLabel>
          <Select
            label={t("applications.statusFilter")}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          >
            <MenuItem value="ALL">{t("applications.allStatuses")}</MenuItem>
            <MenuItem value="PENDING">{t("applications.status.PENDING")}</MenuItem>
            <MenuItem value="APPROVED">{t("applications.status.APPROVED")}</MenuItem>
            <MenuItem value="REJECTED">{t("applications.status.REJECTED")}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {apps.isLoading ? (
        <CircularProgress />
      ) : rows.length === 0 ? (
        <Alert severity="info">{t("applications.empty")}</Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("applications.col.submitted")}</TableCell>
                <TableCell>{t("applications.col.applicant")}</TableCell>
                <TableCell>{t("applications.col.rpps")}</TableCell>
                <TableCell>{t("applications.col.email")}</TableCell>
                <TableCell>{t("applications.col.org")}</TableCell>
                <TableCell>{t("applications.col.status")}</TableCell>
                <TableCell align="right">{t("applications.col.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>
                    {new Date(a.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {a.first_name ? `${a.first_name} ${a.last_name}` : a.last_name}
                    </Typography>
                    {a.message && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          fontStyle: "italic",
                          maxWidth: 280,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {a.message}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>
                    {a.rpps ?? "—"}
                  </TableCell>
                  <TableCell>{a.email}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>
                    {a.organization_code}
                    {!a.organization_id && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={t("applications.unresolvedOrg")}
                        sx={{ ml: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={STATUS_COLOR[a.status] ?? "default"}
                      label={t(`applications.status.${a.status}`)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {a.status === "PENDING" && (
                      <>
                        <Tooltip title={t("applications.approve")}>
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                setTarget(a);
                                setAction("approve");
                                setNote("");
                                setErrorDetail(null);
                              }}
                              disabled={!a.organization_id}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t("applications.reject")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setTarget(a);
                              setAction("reject");
                              setNote("");
                              setErrorDetail(null);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog
        open={target !== null}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <ContactMailIcon
            fontSize="small"
            sx={{ verticalAlign: "middle", mr: 1, color: "primary.main" }}
          />
          {action === "approve" ? t("applications.approveTitle") : t("applications.rejectTitle")}
        </DialogTitle>
        <DialogContent>
          {target && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {errorDetail && (
                <Alert severity="error">
                  {t(`applications.err.${errorDetail}`, {
                    defaultValue: errorDetail,
                  })}
                </Alert>
              )}
              <Typography variant="body2">
                <strong>
                  {target.first_name
                    ? `${target.first_name} ${target.last_name}`
                    : target.last_name}
                </strong>
                {" — "}
                {target.email}
              </Typography>
              {action === "approve" && (
                <>
                  <FormControl size="small" fullWidth>
                    <InputLabel>{t("applications.membershipKind")}</InputLabel>
                    <Select
                      label={t("applications.membershipKind")}
                      value={membershipKind}
                      onChange={(e) =>
                        setMembershipKind(
                          e.target.value as typeof membershipKind,
                        )
                      }
                    >
                      <MenuItem value="SALARIE">SALARIE</MenuItem>
                      <MenuItem value="LIBERAL">LIBERAL</MenuItem>
                      <MenuItem value="VACATAIRE">VACATAIRE</MenuItem>
                      <MenuItem value="STAGIAIRE">STAGIAIRE</MenuItem>
                      <MenuItem value="RESERVISTE">RESERVISTE</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={createInvite}
                        onChange={(e) => setCreateInvite(e.target.checked)}
                      />
                    }
                    label={t("applications.createInvite")}
                  />
                </>
              )}
              <TextField
                label={
                  action === "approve"
                    ? t("applications.noteOpt")
                    : t("applications.rejectReason")
                }
                value={note}
                onChange={(e) => setNote(e.target.value)}
                multiline
                minRows={2}
                maxRows={4}
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTarget(null);
              setAction(null);
            }}
          >
            {t("common.cancel")}
          </Button>
          {action === "approve" ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending}
            >
              {t("applications.confirmApprove")}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              startIcon={<CloseIcon />}
              onClick={() => rejectMutation.mutate()}
              disabled={rejectMutation.isPending}
            >
              {t("applications.confirmReject")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
